import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { size, rem } from 'polished';
import screen from 'superior-mq';
import { bp, hover } from 'Styles/helpers';
import objectPath from 'object-path';
import Section from '../../section';
import Container from '../../container';
import HomeH3 from '../HomeH3';
import LazyImg from '../../lazy-img';
import Twitter from '../../svg/twitter.svg';
import Facebook from '../../svg/facebook.svg';
import Instagram from '../../svg/instagram.svg';
import UnstyledList from '../../unstyled-list';
import Waves from '../../waves';

const sectionStyles = `
  position: relative;
  overflow: hidden;
`;

const Connect = ({ title, images }) => {
  const [image1, image2, image3] = images;
  const imageParams = '?w=180&h=180&fit=thumb&q=70&fm=jpg&fl=progressive';

  return (
    <Section styles={sectionStyles} dark>
      <Waves position="top" alignment="bottom" height={1796} color="#fff" />
      <Container>
        <ContentWrap>

          <VertH3>{title}</VertH3>

          <ConnectGrid>
            {objectPath.has(image1, 'fields.file.url') &&
              <ConnectImage col="2" row="1">
                <LazyImg
                  width={180}
                  height={180}
                  src={`${image1.fields.file.url}${imageParams}`}
                  alt={image1.fields.description}
                />
              </ConnectImage>
            }

            {objectPath.has(image2, 'fields.file.url') &&
              <ConnectImage col="1" row="6" style={{ zIndex: 0 }}>
                <LazyImg
                  width={180}
                  height={180}
                  src={`${image2.fields.file.url}${imageParams}`}
                  alt={image2.fields.description}
                />
              </ConnectImage>
            }

            {objectPath.has(image3, 'fields.file.url') &&
              <ConnectImage col="2" row="10" over>
                <LazyImg
                  width={180}
                  height={180}
                  src={`${image3.fields.file.url}${imageParams}`}
                  alt={image3.fields.description}
                />
              </ConnectImage>
            }

            <SocialList display="block">
              <li>
                <a
                  href="https://www.instagram.com/wavesforwater/"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  aria-label="Waves for Water Instagram"
                >
                  <Instagram />
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/wavesforwater/"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  aria-label="Waves for Water Facebook"
                >
                  <Facebook />
                </a>
              </li>
              <li>
                <a
                  href="https://twitter.com/wavesforwater"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  aria-label="Waves for Water Twitter"
                >
                  <Twitter />
                </a>
              </li>
            </SocialList>
          </ConnectGrid>

        </ContentWrap>
      </Container>

    </Section>
  );
};

const ContentWrap = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VertH3 = styled(HomeH3)`
  writing-mode: vertical-lr;
  white-space: nowrap;
  transform: rotate(180deg);
  z-index: 2;

  ${screen.below(bp.laptop, `
    font-size: ${rem(130)};
  `)}

  ${screen.below(bp.tablet, `
    font-size: ${rem(80)};
  `)}
`;

const ConnectGrid = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  display: grid;
  grid-template-rows: repeat(15, 1fr);
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 20px;
  width: 380px;
  height: 450px;
  margin: auto;

  ${screen.below(bp.tablet, `
    width: 228px;
    height: 270px;
  `)}
`;

const ConnectImage = styled.div`
  grid-column: ${props => props.col} / span 1;
  grid-row: ${props => props.row} / span 6;
  z-index: ${props => props.over ? 3 : 1};

  img { display: block; }
`;

const SocialList = styled(UnstyledList)`
  position: absolute;
  top: 33%;
  right: 50%;
  transform: translatex(50%);
  z-index: 4;

  li:not(:first-of-type) {
    margin-top: 20px;
  }

  ${screen.below(bp.tablet, css`
    top: 25%;

    li:not(:first-of-type) {
      margin-top: 12px;
    }

    svg {
      max-width: 16px;
      max-height: 16px;
    }
  `)}

  a {
    ${size('66px')}
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${props => props.theme.black};
    border-radius: 50%;
    color: ${props => props.theme.blue};

    ${hover('color: #fff')}

    ${screen.below(bp.tablet, css`
      width: 44px;
      height: 44px;
    `)}
  }
`;

Connect.propTypes = {
  title: PropTypes.string,
  images: PropTypes.array,
};

Connect.defaultProps = {
  images: [],
};

export default Connect;
